import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { IngestEventsResponse } from '../lib/types.js';
import { MAX_TOASTS, newestId, toastsFor, type ArrivalToast } from '../lib/ingest-toasts.js';

/**
 * "Stats just came in" (owner ask 2026-09-21): a corner of the screen says
 * whose numbers arrived, from which provider, and how much they moved.
 *
 * How it stays honest and quiet:
 *  - the first answer a tab gets only sets the mark. History is never
 *    announced, so opening the dashboard does not fire a dozen toasts;
 *  - the mark is remembered per browser (localStorage), so a reload does not
 *    re-announce what was already shown. Storage that throws or comes back
 *    empty (a private window, cleared site data) just means the tab seeds
 *    itself from the newest event, as a first visit would;
 *  - a pull that moved no money is not shown at all (lib/ingest-toasts.ts);
 *  - each toast goes on its own after a while, or on the close button. A
 *    click takes you to the Stats page and refreshes it, because the point of
 *    knowing is to look.
 *
 * The poll is light (an id and a handful of rows) and only while the tab is
 * visible: a background tab that wakes up after an hour catches up in one
 * answer instead of having polled 180 times.
 */
const POLL_MS = 20_000;
const DISMISS_MS = 12_000;
const SEEN_KEY = 'traffic:ingest-seen';

function readMark(): string | null {
  try {
    const v = window.localStorage.getItem(SEEN_KEY);
    return v && /^\d{1,19}$/.test(v) ? v : null;
  } catch {
    return null;
  }
}

function writeMark(id: string | null): void {
  if (id == null) return;
  try {
    window.localStorage.setItem(SEEN_KEY, id);
  } catch {
    // A tab that cannot remember still behaves: it seeds itself on the next load.
  }
}

export function StatsToasts({ go }: { go: (to: string) => void }) {
  const qc = useQueryClient();
  // The newest event this browser has accounted for. `null` with `seeded` set
  // means "seeded while there was nothing": the next arrival is genuinely new.
  const mark = useRef<string | null>(readMark());
  const seeded = useRef<boolean>(mark.current != null);
  const shown = useRef<Set<string>>(new Set());
  const [toasts, setToasts] = useState<ArrivalToast[]>([]);

  const events = useQuery({
    queryKey: ['ingest-events'],
    queryFn: () =>
      api.get<IngestEventsResponse>(
        mark.current
          ? `/api/ingest-events?since=${encodeURIComponent(mark.current)}`
          : '/api/ingest-events',
      ),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    // A failed poll is not worth a red anything on screen: it retries.
    retry: false,
  });

  const answer = events.data;
  useEffect(() => {
    if (!answer) return;
    const list = answer.events ?? [];
    const first = !seeded.current;
    seeded.current = true;
    // The first answer only marks where this tab starts.
    if (!first) {
      const fresh = toastsFor(list, mark.current, shown.current);
      if (fresh.length) {
        for (const t of fresh) shown.current.add(t.id);
        setToasts((cur) => [...fresh, ...cur].slice(0, MAX_TOASTS));
      }
    } else {
      for (const e of list) shown.current.add(e.id);
    }
    const next = newestId(list, mark.current) ?? answer.latestId ?? mark.current;
    if (next !== mark.current) {
      mark.current = next;
      writeMark(next);
    }
  }, [answer]);

  // Each toast leaves on its own. One timer per toast, cleared on unmount so a
  // navigation mid-life cannot set state on a gone component.
  useEffect(() => {
    if (!toasts.length) return;
    const timers = toasts.map((t) =>
      window.setTimeout(() => setToasts((cur) => cur.filter((x) => x.id !== t.id)), DISMISS_MS),
    );
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [toasts]);

  if (!toasts.length) return null;
  const open = (t: ArrivalToast) => {
    setToasts((cur) => cur.filter((x) => x.id !== t.id));
    void qc.invalidateQueries({ queryKey: ['summary'] });
    void qc.invalidateQueries({ queryKey: ['leakage'] });
    go('/');
  };
  return (
    // Polite, not assertive: numbers arriving is news, not an alarm, and an
    // assertive region interrupts whatever a screen reader is saying.
    <div className="toasts" role="status" aria-live="polite" aria-label="Stats arrivals">
      {toasts.map((t) => (
        <div className="toast" key={t.id} data-direction={t.direction}>
          <button
            type="button"
            className="toast-body"
            onClick={() => open(t)}
            title="Open the Stats page with the newest numbers"
          >
            <span className="toast-head">
              <span className="toast-title">{t.title}</span>
              <span className={`toast-amount ${t.direction}`}>{t.amount}</span>
            </span>
            <span className="toast-detail">{t.detail}</span>
            {t.relationship ? (
              <span className="toast-detail">relationship {t.relationship}</span>
            ) : null}
            {t.note ? <span className="toast-note">{t.note}</span> : null}
          </button>
          <button
            type="button"
            className="toast-close"
            aria-label={`Dismiss the arrival from ${t.title}`}
            onClick={() => setToasts((cur) => cur.filter((x) => x.id !== t.id))}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
